// apps/admin/src/lib/apiClient.ts
//
// Phase 8.1 Wave 2 Task 08 — canonical axios client for the admin
// portal SPA. Wraps `axios.create()` to enforce:
//
//   1. `withCredentials: true` — JWT pw_token HTTP-only cookie + the
//      XSRF-TOKEN double-submit cookie are sent on every request
//      (admin cookie strategy mirrors Phase 1 + Phase 6).
//   2. Per-request `X-Request-Id` (UUID v4) header for log correlation
//      across the stack (INTERFACE-STANDARDS correlation_id_header).
//   3. CSRF double-submit — server sets `XSRF-TOKEN` (NOT HttpOnly) on
//      login; we re-attach it as `X-XSRF-TOKEN` on every mutating verb
//      (POST/PUT/PATCH/DELETE).
//   4. Envelope unwrap — success responses `{ ok: true, data }` (or the
//      legacy `{ success: true, data }`) are unwrapped at the response
//      interceptor; the consumer receives `data` directly without ever
//      touching the envelope.
//   5. Error transform — every non-2xx body is normalised to the
//      canonical `ApiErrorEnvelope`, then thrown as `ApiError` whose
//      message follows the INTERFACE-STANDARDS priority chain
//      (`user_message > message > network_fallback`).
//   6. 401 single-flight refresh: concurrent 401s collapse to a
//      single POST `/auth/refresh`; on success the original request is
//      replayed once; on failure the registered unauthorized handler
//      (AuthProvider) redirects to `/login?returnUrl=…`.
//   7. Network/abort → `ApiError(0, NETWORK_ERROR)` so callers always
//      branch on a consistent envelope, never on raw AxiosError.
//
// Notes:
//   - `import.meta.env.VITE_API_BASE_URL` is the absolute API origin
//     (e.g. `https://api-v3.printway.work` or `http://localhost:3010`).
//     The interceptor PREPENDS `/api/v1` for relative paths whose
//     callers pass just the contract path (e.g. `/me`, `/admin/vendors`)
//     so the rest of the codebase can drop the version prefix.
//   - Default baseURL stays empty when VITE_API_BASE_URL is unset so
//     the dev Vite proxy intercepts `/api/v1/...` paths.
//
// vg-binding: PLAN:task-08
// vg-binding: P8.D-160
// vg-binding: P8.D-161
// vg-binding: P8.D-162
// vg-binding: P8.D-166
// vg-binding: P8.D-174
// vg-binding: P8.D-212
// vg-binding: P8.D-260
// vg-binding: P8.D-267
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: INTERFACE-STANDARDS:response-envelope
// vg-binding: INTERFACE-STANDARDS:message-priority

/* eslint-disable @typescript-eslint/no-explicit-any */

import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import {
  NETWORK_FALLBACK_MESSAGE,
  type ApiErrorEnvelope,
} from './errorHandler.js';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type { ApiErrorEnvelope } from './errorHandler.js';

/** Canonical success envelope per INTERFACE-STANDARDS.md §response-envelope. */
export interface ApiSuccessEnvelope<T> {
  ok: true;
  data: T;
  message?: string;
  meta?: Record<string, unknown>;
  request_id?: string;
}

export type ApiEnvelope<T> = ApiSuccessEnvelope<T> | ApiErrorEnvelope;

/**
 * Thrown by the public verb helpers so callers can branch on
 * `error.code`. The `message` field is already the INTERFACE-STANDARDS
 * resolved copy (`user_message > message > network_fallback`).
 */
export class ApiError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly userMessage?: string;
  public readonly details?: Record<string, unknown>;
  public readonly fieldErrors?: Record<string, string[]>;
  public readonly requestId?: string;

  constructor(status: number, env: ApiErrorEnvelope) {
    // INTERFACE-STANDARDS message priority: user_message > message > fallback.
    super(env.error.user_message ?? env.error.message ?? NETWORK_FALLBACK_MESSAGE);
    this.name = 'ApiError';
    this.code = env.error.code;
    this.status = status;
    if (env.error.user_message !== undefined) {
      this.userMessage = env.error.user_message;
    }
    if (env.error.details !== undefined) {
      this.details = env.error.details;
    }
    if (env.error.field_errors !== undefined) {
      this.fieldErrors = env.error.field_errors;
    } else {
      // #2705 — a large family of admin handlers reports zod failures as
      // `details: { fieldErrors, formErrors }` (the shape `ZodError.flatten()`
      // returns) rather than the top-level snake_case `field_errors`. Reading
      // only the latter meant those 400s reached the UI as the bare handler
      // message ("Invalid body") with no clue which field was rejected.
      const nested = (env.error.details as { fieldErrors?: unknown } | undefined)
        ?.fieldErrors;
      if (nested && typeof nested === 'object') {
        this.fieldErrors = nested as Record<string, string[]>;
      }
    }
    if (env.error.request_id !== undefined) {
      this.requestId = env.error.request_id;
    }
  }
}

/** Hook fired when `/auth/refresh` fails — AuthProvider attaches a redirector. */
export type UnauthorizedHandler = (returnUrl: string) => void;

// ---------------------------------------------------------------------------
// Module-local state
// ---------------------------------------------------------------------------

const ENV_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

/**
 * Default to relative `/api/v1` so dev proxy + same-origin both work;
 * FE reads `VITE_API_BASE_URL` when targeting the canonical apex
 * (`https://api-v3.printway.work`).
 */
export const API_BASE_URL: string = ENV_BASE.replace(/\/$/, '');

/** Default versioned API prefix. Prepended to relative paths that do
 *  NOT already start with `/api/`. */
const API_PREFIX = '/api/v1';

/** CSRF token cookie name (server emits during `/auth/login`). */
const CSRF_COOKIE_NAME = 'XSRF-TOKEN';

let unauthorizedHandler: UnauthorizedHandler | null = null;

/** Register / clear the global 401-failure redirector. */
export function setUnauthorizedHandler(h: UnauthorizedHandler | null): void {
  unauthorizedHandler = h;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Read non-HttpOnly cookie value by name (CSRF double-submit pattern). */
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + escaped + '=([^;]*)'),
  );
  return match && typeof match[1] === 'string'
    ? decodeURIComponent(match[1])
    : null;
}

function readCsrfToken(): string | null {
  return readCookie(CSRF_COOKIE_NAME) ?? readCookie('csrf_token');
}

/**
 * Exposed CSRF token reader for raw `fetch` callers (file uploads, etc.)
 * that don't go through `axiosClient` or the typed apiClient and therefore
 * miss the auto-injected `X-XSRF-TOKEN` header. Returns null on first paint
 * before the server has set the double-submit cookie.
 */
export function getCsrfToken(): string | null {
  return readCsrfToken();
}

/** RFC 4122 v4 UUID — used as X-Request-Id for log correlation. */
function newRequestId(): string {
  const c = (globalThis as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return 'req_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function isMutationMethod(method?: string): boolean {
  const m = (method ?? 'get').toUpperCase();
  return m === 'POST' || m === 'PUT' || m === 'PATCH' || m === 'DELETE';
}

/**
 * Normalise any thrown / response error body into the canonical
 * `ApiErrorEnvelope` shape. Accepts:
 *   - already-canonical `{ ok: false, error: {...} }`
 *   - legacy `{ success: false, error: {...} }`
 *   - HTTP-only failures (response with no body, network errors)
 */
function normaliseErrorEnvelope(
  status: number,
  body: any,
): ApiErrorEnvelope {
  // Canonical shape — accept as-is.
  if (body && body.ok === false && body.error && typeof body.error === 'object') {
    return body as ApiErrorEnvelope;
  }

  // Legacy `{ success: false, error: { code, message } }` shape — translate.
  if (
    body &&
    body.success === false &&
    body.error &&
    typeof body.error === 'object'
  ) {
    const err = body.error;
    return {
      ok: false,
      error: {
        code: String(err.code ?? 'HTTP_' + status),
        message: String(err.message ?? err.user_message ?? NETWORK_FALLBACK_MESSAGE),
        user_message: err.user_message,
        details: err.details,
        field_errors: err.field_errors,
        request_id: err.request_id ?? body.requestId ?? body.request_id,
      },
    };
  }

  // No body / opaque shape — synthesise from HTTP status.
  return {
    ok: false,
    error: {
      code: status === 0 ? 'NETWORK_ERROR' : 'HTTP_' + String(status),
      message: NETWORK_FALLBACK_MESSAGE,
    },
  };
}

// ---------------------------------------------------------------------------
// Single-flight 401 refresh
// ---------------------------------------------------------------------------

/**
 * Single-flight refresh state. Multiple concurrent 401s collapse to
 * ONE `POST /auth/refresh` round-trip — every waiting promise resolves
 * to the same boolean (true = refresh succeeded).
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const url = buildAbsoluteUrl('/auth/refresh');
      const csrf = readCsrfToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Request-Id': newRequestId(),
      };
      if (csrf) headers['X-XSRF-TOKEN'] = csrf;

      // Bypass our own interceptor stack — raw fetch keeps the 401
      // handler from re-entering and looping.
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({}),
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      // Clear the slot AFTER queue drains so near-simultaneous calls
      // still piggy-back. setTimeout(0) is enough.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();

  return refreshInFlight;
}

// ---------------------------------------------------------------------------
// URL builder
// ---------------------------------------------------------------------------

/**
 * Build an absolute URL from a relative path. Rules:
 *   - Absolute URLs (`http(s)://...`) pass through untouched.
 *   - Paths already starting with `/api/` are joined with `API_BASE_URL`
 *     verbatim (caller already supplied the version).
 *   - Bare paths (e.g. `/me`, `/admin/vendors`) are prefixed with
 *     `${API_BASE_URL}/api/v1`.
 */
function buildAbsoluteUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  let p = path.startsWith('/') ? path : '/' + path;
  if (!p.startsWith('/api/')) p = API_PREFIX + p;
  return API_BASE_URL + p;
}

// ---------------------------------------------------------------------------
// Axios instance + interceptors
// ---------------------------------------------------------------------------

/**
 * The canonical admin-portal axios instance. Direct callers should use
 * the `apiClient` verb helpers (below) — they layer the envelope unwrap
 * + ApiError throw on top of the axios response. Use this instance only
 * when you need access to the raw `AxiosResponse<T>` (e.g. download
 * streaming).
 */
export const axiosClient: AxiosInstance = axios.create({
  // Base URL is empty when no env var; the request interceptor expands
  // every path to an absolute URL using `buildAbsoluteUrl` so dev +
  // prod both work without touching baseURL config per environment.
  baseURL: '',
  withCredentials: true,
  timeout: 30_000,
  headers: {
    Accept: 'application/json',
  },
});

axiosClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Resolve absolute URL from the caller's (possibly relative) path.
    if (config.url) {
      config.url = buildAbsoluteUrl(config.url);
    }

    // Ensure AxiosHeaders so `.set` is available across both v0.x +
    // v1.x runtime shapes.
    const headers = AxiosHeaders.from(config.headers ?? {});
    headers.set('X-Request-Id', newRequestId());

    // JSON content-type for non-FormData bodies.
    if (
      config.data !== undefined &&
      !(config.data instanceof FormData) &&
      !headers.has('Content-Type')
    ) {
      headers.set('Content-Type', 'application/json');
    }

    // CSRF double-submit on every mutating verb.
    if (isMutationMethod(config.method)) {
      const csrf = readCsrfToken();
      if (csrf) headers.set('X-XSRF-TOKEN', csrf);
    }

    config.headers = headers;
    return config;
  },
);

axiosClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Envelope unwrap — success responses return `data` directly.
    // 204 No Content stays `null`.
    if (response.status === 204) {
      return response;
    }

    const body = response.data;
    if (body && typeof body === 'object') {
      // Canonical envelope.
      if ((body as any).ok === true && 'data' in (body as any)) {
        // Stash envelope `meta` so getWithMeta() can surface pagination
        // totals; default get() still returns only `data`. Debug
        // dbg-20260529-2009 (list views needed meta.total for paging).
        (response as { __vgMeta?: unknown }).__vgMeta = (
          body as ApiSuccessEnvelope<unknown>
        ).meta;
        response.data = (body as ApiSuccessEnvelope<unknown>).data;
        return response;
      }
      // Legacy `{ success: true, data }` envelope.
      if ((body as any).success === true && 'data' in (body as any)) {
        (response as { __vgMeta?: unknown }).__vgMeta = (body as any).meta;
        response.data = (body as any).data;
        return response;
      }
      // Server returned an in-band error inside a 2xx response — treat
      // as a domain failure per INTERFACE-STANDARDS http_policy.
      if (
        (body as any).ok === false ||
        (body as any).success === false
      ) {
        const env = normaliseErrorEnvelope(response.status, body);
        throw new ApiError(response.status, env);
      }
    }
    // Unknown shape — return raw response so binary / non-envelope
    // endpoints (file downloads) still work.
    return response;
  },
  async (error: AxiosError | ApiError | unknown) => {
    // Re-throw our own ApiError unchanged (already-normalised).
    if (error instanceof ApiError) {
      throw error;
    }

    // Network / abort / CORS — no response, no body.
    if (!axios.isAxiosError(error) || !error.response) {
      const code = (error as any)?.code;
      const env: ApiErrorEnvelope = {
        ok: false,
        error: {
          code: code === 'ECONNABORTED' ? 'TIMEOUT' : 'NETWORK_ERROR',
          message: NETWORK_FALLBACK_MESSAGE,
        },
      };
      throw new ApiError(0, env);
    }

    const ax = error as AxiosError;
    const status = ax.response?.status ?? 0;

    // 401 single-flight refresh + retry — once per original request.
    const cfg = ax.config as (InternalAxiosRequestConfig & {
      _retry?: boolean;
      _skipAuthRefresh?: boolean;
    }) | undefined;

    if (status === 401 && cfg && !cfg._retry && !cfg._skipAuthRefresh) {
      cfg._retry = true;
      const refreshed = await refreshTokens();
      if (refreshed) {
        // Replay the original request once. The interceptor stack
        // re-runs (fresh X-Request-Id, fresh CSRF read).
        return axiosClient.request(cfg);
      }
      // Refresh failed → AuthProvider can take over.
      if (unauthorizedHandler) {
        const returnUrl =
          typeof window !== 'undefined'
            ? window.location.pathname + window.location.search
            : '/';
        unauthorizedHandler(returnUrl);
      }
      // Fall through — throw the 401 envelope so caller still sees the
      // SESSION_EXPIRED branch.
    }

    const env = normaliseErrorEnvelope(status, ax.response?.data);
    throw new ApiError(status, env);
  },
);

// ---------------------------------------------------------------------------
// Public verb helpers
// ---------------------------------------------------------------------------

export interface ApiRequestOptions {
  headers?: Record<string, string>;
  params?: Record<string, unknown>;
  signal?: AbortSignal;
  /** Bypass auto-refresh on 401 (used by /auth/refresh itself). */
  skipAuthRefresh?: boolean;
  /** Bypass envelope unwrap; return raw AxiosResponse (binary downloads). */
  raw?: boolean;
  /** Optional idempotency key (POST/PUT/PATCH/DELETE retry-safety). */
  idempotencyKey?: string;
  /** Override the request timeout in ms. */
  timeout?: number;
}

function toAxiosConfig(
  opts: ApiRequestOptions | undefined,
): AxiosRequestConfig {
  const cfg: AxiosRequestConfig & {
    _skipAuthRefresh?: boolean;
  } = {};
  if (opts?.headers) cfg.headers = { ...opts.headers };
  if (opts?.params) cfg.params = opts.params;
  if (opts?.signal) cfg.signal = opts.signal;
  if (opts?.timeout !== undefined) cfg.timeout = opts.timeout;
  if (opts?.skipAuthRefresh) cfg._skipAuthRefresh = true;
  if (opts?.idempotencyKey) {
    cfg.headers = { ...(cfg.headers ?? {}), 'Idempotency-Key': opts.idempotencyKey };
  }
  if (opts?.raw) {
    cfg.responseType = 'blob';
  }
  return cfg;
}

/**
 * Public API surface — wraps the axios instance so consumers receive
 * the unwrapped `data` payload directly (or throw `ApiError`).
 *
 * Usage:
 *   const vendors = await apiClient.get<Vendor[]>('/admin/vendors');
 *   await apiClient.post('/admin/users', { email });
 */
export const apiClient = {
  async get<T = unknown>(
    path: string,
    opts?: ApiRequestOptions,
  ): Promise<T> {
    const res = await axiosClient.get<T>(path, toAxiosConfig(opts));
    return res.data;
  },
  /**
   * Like get(), but also returns the envelope `meta` (e.g. pagination
   * `{ total, page, pageSize }`). The default get() drops meta for
   * back-compat; list views that paginate need it. Debug dbg-20260529-2009.
   */
  async getWithMeta<T = unknown, M = Record<string, unknown>>(
    path: string,
    opts?: ApiRequestOptions,
  ): Promise<{ data: T; meta: M | undefined }> {
    const res = await axiosClient.get<T>(path, toAxiosConfig(opts));
    const meta = (res as { __vgMeta?: M }).__vgMeta;
    return { data: res.data, meta };
  },
  async post<T = unknown>(
    path: string,
    body?: unknown,
    opts?: ApiRequestOptions,
  ): Promise<T> {
    const res = await axiosClient.post<T>(path, body, toAxiosConfig(opts));
    return res.data;
  },
  async put<T = unknown>(
    path: string,
    body?: unknown,
    opts?: ApiRequestOptions,
  ): Promise<T> {
    const res = await axiosClient.put<T>(path, body, toAxiosConfig(opts));
    return res.data;
  },
  async patch<T = unknown>(
    path: string,
    body?: unknown,
    opts?: ApiRequestOptions,
  ): Promise<T> {
    const res = await axiosClient.patch<T>(path, body, toAxiosConfig(opts));
    return res.data;
  },
  async delete<T = unknown>(
    path: string,
    bodyOrOpts?: unknown | ApiRequestOptions,
    opts?: ApiRequestOptions,
  ): Promise<T> {
    // Allow `delete(path, body, opts)` so callers can attach a JSON body
    // (force-delete handlers need `{ reason, code, confirm_merchant_name }`)
    // while staying backwards-compatible with `delete(path, opts?)`.
    let body: unknown = undefined;
    let resolvedOpts: ApiRequestOptions | undefined;
    if (
      bodyOrOpts &&
      typeof bodyOrOpts === 'object' &&
      !Array.isArray(bodyOrOpts) &&
      (
        'headers' in bodyOrOpts ||
        'params' in bodyOrOpts ||
        'signal' in bodyOrOpts ||
        'skipAuthRefresh' in bodyOrOpts ||
        'raw' in bodyOrOpts ||
        'idempotencyKey' in bodyOrOpts ||
        'timeout' in bodyOrOpts
      ) &&
      opts === undefined
    ) {
      resolvedOpts = bodyOrOpts as ApiRequestOptions;
    } else {
      body = bodyOrOpts;
      resolvedOpts = opts;
    }
    const cfg = toAxiosConfig(resolvedOpts);
    if (body !== undefined) cfg.data = body;
    const res = await axiosClient.delete<T>(path, cfg);
    return res.data;
  },
} as const;

export default apiClient;
